import React, { Fragment, useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { PropTypes } from 'prop-types';
import _get from 'lodash/get';
import MLIcon from '../MLIcon/MLIcon';
import { useStyles } from './CustomMuiDropdownStyles';
import { getTestName } from '../../src/util/util';

const CustomMuiDropdown = React.forwardRef((props, ref) => {
  const {
    title,
    items,
    onClick,
    ariaLabel,
    onClickItem,
    onClose,
    valueProp,
    labelProp,
    defaultItem,
    defaultIndex,
    defaultValue,
    isMenu,
    disabled,
    className,
    optionsClassName,
    labelClassName,
    selectedValue,
    placeholder,
    dataTestId,
    anchorOrigin,
    transformOrigin,
    keepOpen,
  } = props;

  const classes = useStyles();
  let initialItem = defaultIndex ? items[defaultIndex] : defaultItem;
  if (defaultValue) initialItem = items.find(item => _get(item, valueProp) === defaultValue);
  const [selectedItem, setSelectedItem] = useState(initialItem);
  const selectedItemValue = _get(selectedItem, valueProp, (initialItem ? initialItem.value : null));
  const selectedItemLabel = _get(selectedItem, labelProp, (initialItem ? initialItem.label : placeholder));

  const anchorProps = {
    anchorOrigin,
    transformOrigin,
    getContentAnchorEl: null,
  };

  useEffect(() => {
    const selected = items.find(item => _get(item, valueProp) === selectedValue);
    setSelectedItem(selected);
  }, [selectedValue, disabled, items]);

  const text = isMenu ? title : selectedItemLabel;
  const [anchorEl, setAnchorEl] = useState(null);

  const filtered = items.filter(x => !x.hide);
  const last = filtered.length - 1;

  const handleClose = (event) => {
    if (onClose) onClose(event);
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    if (onClick) onClick(event);
    setAnchorEl(event.currentTarget);
  };

  const handleClickItem = (item, event) => {
    if (onClickItem) onClickItem(item, event);
    setSelectedItem(item);
    if (!keepOpen) {
      setAnchorEl(null);
    }
  };

  return (
    <Fragment>
      <Button
        ref={ref}
        color="secondary"
        variant="outlined"
        aria-controls="assignment-view-menu"
        aria-haspopup="true"
        aria-label={ariaLabel || text}
        onClick={handleClick}
        data-test-id={dataTestId || "menu_dropdown_button"}
        disabled={disabled}
        classes={{ root: classes.button, label: classes.label }}
        className={className}
        disableRipple
      >
        <p className={`${classes.buttonText} ${labelClassName}`}>{text}</p>
        <MLIcon
          className={classes.menuBtnIcon}
          title={title}
          data-id="Toggle MenuDropdown"
          desc="Toggle MenuDropdown"
          type="caret_down"
        />
      </Button>
      {!disabled && <Menu
        id="assignment-view-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        classes={{ paper: `${classes.paper} ${optionsClassName}`, list: classes.list }}
        elevation={2}
        keepMounted
        transitionDuration={0}
        {...(anchorOrigin && transformOrigin ? anchorProps : {})}
        PaperProps={{
          'data-test-id': dataTestId || "menu-dropdown-container",
        }}
      >
        {
          filtered
            .map((item, index) => {
              const itemLabel = _get(item, labelProp, '');
              const itemValue = _get(item, valueProp, '');
              return (
                <MenuItem
                  key={index}
                  value={itemValue}
                  onClick={event => handleClickItem(item, event)}
                  classes={{ root: classes.menuItem }}
                  disableRipple
                  divider={index !== last}
                  data-test-id={`menu_item_${getTestName(itemValue)}`}
                >
                  <div className={classes.labelText}>{itemLabel}</div>
                  {!isMenu && itemValue === selectedItemValue &&
                    <MLIcon type="check" className={classes.menuItemIcon} />
                  }
                </MenuItem>
              );
            })
        }
      </Menu>}
    </Fragment>
  );
});

CustomMuiDropdown.defaultProps = {
  labelProp: 'label',
  valueProp: 'value',
  defaultIndex: null,
  defaultValue: null,
  defaultItem: null,
  isMenu: false,
  items: [],
  selectedValue: null,
  placeholder: '',
  optionsClassName: '',
  labelClassName: '',
};

CustomMuiDropdown.propTypes = {
  valueProp: PropTypes.string,
  labelProp: PropTypes.string,
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func,
  onClickItem: PropTypes.func,
  onClose: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  defaultItem: PropTypes.object,
  defaultIndex: PropTypes.number,
  defaultValue: PropTypes.any,
  isMenu: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  optionsClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  selectedValue: PropTypes.any,
  placeholder: PropTypes.string,
  dataTestId: PropTypes.string,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
};

export default CustomMuiDropdown;
